import { useState } from 'react';
import { Trans } from 'react-i18next';
import isElectron from 'is-electron';
import { useHistory, useParams } from 'react-router-dom';

import { setMicPermission } from 'flow/actions/app-actions';
import { setSoftphoneShouldBeRegistered } from 'flow/actions/phone-actions';
import Page from 'flow/components/Setting/Page';
import SettingRow from 'flow/components/Setting/SettingRow';
import SettingSection from 'flow/components/Setting/SettingSection';
import Divider from 'flow/components/ui-components/Divider';
import InfoBox, { InfoBoxType } from 'flow/components/ui-components/InfoBox';
import RadioButton from 'flow/components/ui-components/RadioButton';
import Toggle from 'flow/components/ui-components/Toggle';
import SoftphoneIncomingCallsSkeleton from 'flow/components/loaders/SoftphoneIncomingCallsSkeleton';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { useUserCallRights } from 'flow/hooks/api/useUserRightsSetting';
import useTempSessionToggle from 'flow/hooks/useTempSessionToggle';
import {
    UserAppSettingOption,
    useIncomingCallSettings,
    useUpdateIncomingCallSetting,
} from 'flow/hooks/api/useSoftphoneCallSettings';
import { selectHasCapability } from 'flow/selectors/ExtensionSelectors';
import { selectTemporaryPasswordState } from 'flow/selectors/app-selectors';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import {
    addDeviceLogEntry,
    checkBrowserMicrophonePermission,
    getSoftphoneShouldBeRegistered,
} from 'flow/utils/browser-utils';
import { checkElectronMicrophonePermission } from 'flow/utils/electron-utils';
import { Capabilities } from 'flow/utils/capability-utils';
import { generatePath } from 'flow/utils/router-utils';

const SoftphoneIncomingCalls = () => {
    const { i18next, phoneEnabled } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { extensionKey } = useParams<{ extensionKey: ExtensionKey }>();

    const hasCapabilitySoftphone = useAppSelector((state) =>
        selectHasCapability(state, extensionKey, Capabilities.SOFTPHONE),
    );

    const loggedInWithTempPass = useAppSelector(selectTemporaryPasswordState);

    const [localRegistered, setLocalRegistered] = useState(
        getSoftphoneShouldBeRegistered(loggedInWithTempPass),
    );
    const { tempSessionActive, handleTempSessionToggle } =
        useTempSessionToggle();

    const { data: incomingSettings, isLoading } = useIncomingCallSettings();
    const { mutate: updateIncomingCallSetting } =
        useUpdateIncomingCallSetting();

    const settingOption = incomingSettings?.value;
    const useSettingEverywhere =
        settingOption === UserAppSettingOption.ON ||
        settingOption === UserAppSettingOption.OFF;

    // Derive directly from the API value when available to avoid a flash
    // caused by the useEffect sync cycle. Fall back to local state in
    // per-client mode or while the API is still loading.
    const isSoftphoneRegistered = useSettingEverywhere
        ? settingOption === UserAppSettingOption.ON
        : localRegistered;

    const handleUseSettingEverywhereChange = (useEverywhere: boolean) => {
        const registeredSetting = isSoftphoneRegistered
            ? UserAppSettingOption.ON
            : UserAppSettingOption.OFF;
        const newSetting = useEverywhere
            ? registeredSetting
            : UserAppSettingOption.PER_CLIENT;
        updateIncomingCallSetting(newSetting);
    };

    const toggleSoftPhonePromise = async () => {
        if (!loggedInWithTempPass) {
            if (!isSoftphoneRegistered) {
                const permissionPrompt = isElectron()
                    ? await checkElectronMicrophonePermission()
                    : await checkBrowserMicrophonePermission();
                if (!permissionPrompt) {
                    addDeviceLogEntry('User denied microphone access');
                    dispatch(setMicPermission(false));
                    return;
                }
            }
            dispatch(setMicPermission(true));
            addDeviceLogEntry('User toggled microphone access on');
            dispatch(setSoftphoneShouldBeRegistered(!isSoftphoneRegistered));
        }

        setLocalRegistered((value: boolean) => !value);

        if (useSettingEverywhere) {
            updateIncomingCallSetting(
                isSoftphoneRegistered
                    ? UserAppSettingOption.OFF
                    : UserAppSettingOption.ON,
            );
        }
    };

    const { canConfigureIncomingCalls } = useUserCallRights();
    const isReadOnly =
        !hasCapabilitySoftphone || !phoneEnabled || !canConfigureIncomingCalls;

    return (
        <Page
            header="settings.softphone.incoming_calls"
            onClickBack={() =>
                history.push(
                    generatePath(routes['settings.softphone'], {
                        extensionKey,
                    }),
                )
            }
        >
            {isLoading ? (
                <SoftphoneIncomingCallsSkeleton />
            ) : (
                <>
                    <div
                        className={
                            isReadOnly ? 'pointer-events-none opacity-50' : ''
                        }
                    >
                        {isReadOnly && (
                            <InfoBox type={InfoBoxType.Info} className="mb-2">
                                {i18next.t('settings.softphone.no_permission')}
                            </InfoBox>
                        )}
                        <SettingRow
                            cta={
                                <Toggle
                                    value={isSoftphoneRegistered}
                                    onChange={toggleSoftPhonePromise}
                                    disabled={isReadOnly}
                                    text={i18next.t(
                                        'media.settings.receivecalls',
                                    )}
                                />
                            }
                        >
                            {i18next.t('media.settings.receivecalls')}
                        </SettingRow>

                        <Divider className="my-2" />

                        <SettingSection>
                            {i18next.t('settings.softphone.options')}
                        </SettingSection>

                        <RadioButton
                            name="incoming-calls-option"
                            checked={!useSettingEverywhere}
                            disabled={isReadOnly}
                            onChange={() =>
                                handleUseSettingEverywhereChange(false)
                            }
                        >
                            {i18next.t(
                                'settings.softphone.configure_per_client',
                            )}
                        </RadioButton>

                        <RadioButton
                            name="incoming-calls-option"
                            checked={useSettingEverywhere}
                            disabled={isReadOnly}
                            onChange={() =>
                                handleUseSettingEverywhereChange(true)
                            }
                        >
                            {i18next.t(
                                'settings.softphone.use_setting_everywhere',
                            )}
                        </RadioButton>
                    </div>

                    {loggedInWithTempPass && (
                        <>
                            <Divider />
                            <InfoBox
                                type={InfoBoxType.Warning}
                                className="flex grow items-center justify-between"
                            >
                                <span>
                                    <Trans
                                        i18n={i18next}
                                        i18nKey={
                                            tempSessionActive
                                                ? 'settings.softphone.temp_login_calls_on'
                                                : 'settings.softphone.temp_login_calls_off'
                                        }
                                        components={[<strong key="0" />]}
                                    />
                                </span>
                                <button
                                    type="button"
                                    className="font-bold"
                                    onClick={handleTempSessionToggle}
                                >
                                    {i18next.t(
                                        tempSessionActive
                                            ? 'settings.softphone.temp_login_stop_receiving'
                                            : 'settings.softphone.temp_login_start_receiving',
                                    )}
                                </button>
                            </InfoBox>
                        </>
                    )}
                </>
            )}
        </Page>
    );
};

export default SoftphoneIncomingCalls;
